import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useUser } from "../context/userContext";
import { extractErrorMessage } from "../utils/apiError";
import { setPageTitle } from "../utils/pageTitle";
import { toast } from "../utils/toast";
import PasswordField from "../components/common/PasswordField";
import GoogleSignInButton from "../components/common/GoogleSignInButton";
import { decodeGoogleCredentialName } from "../utils/googleIdentity";
import { isEmailValid } from "../utils/validation";
import { SUPPORTED_LANGS, DEFAULT_LANG, normalizeLang, translations } from "../utils/i18n";

export default function LoginPage() {
  const { login, loginWithGoogle } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const { lang: langParam } = useParams();
  const language = normalizeLang(langParam);
  const tr = translations[language];
  const [email, setEmail] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const emailInvalid = emailTouched && !isEmailValid(email);

  useEffect(() => {
    setPageTitle("Log In");
  }, []);

  useEffect(() => {
    if (location.state?.verified) {
      toast.success(tr.login.verifiedNotice);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!SUPPORTED_LANGS.includes(langParam)) {
      navigate(`/login/${DEFAULT_LANG}`, { replace: true });
    }
  }, [langParam, navigate]);

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const me = await login(email, password);
      navigate(me.is_onboarded ? "/transcribe" : `/onboarding/${language}`);
    } catch (err) {
      if (err?.response?.data?.error?.code === "email_not_verified") {
        navigate(`/verify-email/${language}`, { state: { email, autoResend: true } });
        return;
      }
      toast.error(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleGoogleCredential(idToken) {
    try {
      const me = await loginWithGoogle(idToken);
      if (me.is_onboarded) {
        navigate("/transcribe");
        return;
      }
      const googleFullName = me.full_name || decodeGoogleCredentialName(idToken);
      navigate(`/onboarding/${language}`, { state: { googleFullName } });
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  return (
    <div lang={language} dir={language === "ar" ? "rtl" : "ltr"} className="page-narrow auth-page">
      <div className="auth-brand">
        <Link to="/" className="auth-brand-mark">
          <img src="/logo-mark.png" alt="" className="brand-mark" />
          {tr.common.brandName}
        </Link>
      </div>
      <div className="auth-content">
        <div className="auth-card">
          <h1>{tr.login.title}</h1>
          <GoogleSignInButton onCredential={handleGoogleCredential} locale={language} />
          <hr className="auth-divider" />
          <form onSubmit={handleSubmit}>
            <div className="field">
              <label htmlFor="email">{tr.common.emailLabel}</label>
              <input
                id="email"
                type="email"
                className={emailInvalid ? "is-invalid" : undefined}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => setEmailTouched(true)}
                autoComplete="email"
                required
              />
            </div>
            <PasswordField
              id="password"
              label={tr.common.passwordLabel}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
            <button
              type="submit"
              className="btn-full"
              disabled={submitting || !isEmailValid(email) || !password}
            >
              {submitting ? tr.login.submitting : tr.login.submit}
            </button>
          </form>
        </div>
        <p className="subtitle auth-footer">
          {tr.login.noAccount} <Link to={`/signup/${language}`}>{tr.login.signUpLink}</Link>
        </p>
      </div>
    </div>
  );
}
